import { add, always, ifElse, init, last, map, max, path, pipe, reduce, sum } from 'ramda';
import { isFalsy } from 'ramda-adjunct';
import { getId } from '../../../utils/common';
import { getComputedPropertyValueAsNumber } from '../../../utils/dom/getComputedProperyValue';
import { getTextWidth } from '../../../utils/dom/getTextWidth';
import { MediaManagerSDKFile, MediaManagerSDKSmartGridSize } from '../../../utils/media-manager-sdk';
import {
  isShutterstockItem,
  isTransparentVideoFile,
  isVideoFile,
  MAX_SHUTTERSTOCK_THUMB_SIZE_WITHOUT_WATERMARK,
  SMART_GRID_ROW_HEIGHT,
  SMART_GRID_THUMB_MAX_WIDTH,
  SMART_GRID_THUMB_MIN_WIDTH,
} from '../../../utils/media-manager-sdk/items';
import { memoize } from '../../../utils/memoize';
import { getExtension } from '../../../utils/path';
import { PRICE_BUTTON_BORDER_AND_PADDING, PRICE_BUTTON_MARGIN, WIDTH_WITHOUT_PRICE } from './constants';

const getItemResizedWidth = (item: MediaManagerSDKFile): number => path(['smartGridSize', 'width'], item);
const getRowWidths = map<MediaManagerSDKFile, number>(getItemResizedWidth);
const calculateRowWidth = pipe<MediaManagerSDKFile[], number[], number>(getRowWidths, sum);

export const resizeItem = (
  item: MediaManagerSDKFile,
  { rowHeight, square }: { rowHeight: number; square?: boolean },
  calculateThumbMinWidth: (item: MediaManagerSDKFile) => number,
): MediaManagerSDKSmartGridSize => {
  const { width, height } = item;

  const fileInfo = item.fileInfo || { aspect: 1 };

  const ratio = square ? 1 : width / height || fileInfo.aspect;
  const newWidth = Math.ceil(rowHeight * ratio);

  return {
    width: isTransparentVideoFile(item)
      ? rowHeight
      : Math.max(
          calculateThumbMinWidth(item),
          Math.min(
            SMART_GRID_THUMB_MAX_WIDTH,
            isShutterstockItem(item)
              ? isVideoFile(item)
                ? Math.max(MAX_SHUTTERSTOCK_THUMB_SIZE_WITHOUT_WATERMARK, newWidth)
                : Math.min(MAX_SHUTTERSTOCK_THUMB_SIZE_WITHOUT_WATERMARK, newWidth)
              : newWidth,
          ),
        ),
    height: rowHeight,
    ratio,
  };
};

export const smartSplitResizedItemsOnRows = (
  items: MediaManagerSDKFile[],
  {
    rowWidth,
    itemsGap,
    square,
  }: {
    rowWidth: number;
    itemsGap: number;
    square?: boolean;
  },
  calculateThumbMinWidth: (item: MediaManagerSDKFile) => number,
): MediaManagerSDKFile[][] =>
  reduce(
    (accRows, item) => {
      const firstRows = init(accRows);
      const lastRow = last(accRows);

      if (!lastRow) {
        // put item on new row if it's empty or first row
        return [...firstRows, [item]];
      }

      const lastRowWidthWithGaps = calculateRowWidth(lastRow) + itemsGap * (lastRow.length - 1);

      if (lastRowWidthWithGaps >= rowWidth) {
        // put item on new row if row is already too wide
        return [...accRows, [item]];
      }

      if (
        (square && lastRowWidthWithGaps + getItemResizedWidth(item) > rowWidth) ||
        lastRowWidthWithGaps + calculateThumbMinWidth(item) > rowWidth
      ) {
        // we are going to make row longer than it is, but we don't want that
        return [...accRows, [item]];
      }

      // add item to last row
      return [...firstRows, [...lastRow, item]];
    },
    [],
    items,
  );

export interface ISplitItemsOnRowsConfig {
  rowWidth: number;
  rowHeight: number;
  itemsGap: number;
  square?: boolean;
}

const addSmartGridResize = memoize((item, config, calculateThumbMinWidth) => ({
  ...item,
  smartGridSize: resizeItem(item, config, calculateThumbMinWidth),
}));

export const splitItemsOnRows: (
  items: MediaManagerSDKFile[],
  config: ISplitItemsOnRowsConfig,
  calculateThumbMinWidth: (item: MediaManagerSDKFile) => number,
) => MediaManagerSDKFile[][] = memoize(
  (
    items: MediaManagerSDKFile[],
    config: ISplitItemsOnRowsConfig,
    calculateThumbMinWidth: (item: MediaManagerSDKFile) => number,
  ): MediaManagerSDKFile[][] =>
    pipe<MediaManagerSDKFile[], MediaManagerSDKFile[], MediaManagerSDKFile[][]>(
      map((item) => addSmartGridResize(item, config, calculateThumbMinWidth)),
      (resizedItems) => smartSplitResizedItemsOnRows(resizedItems, config, calculateThumbMinWidth),
    )(items),
);

export const shouldSmartGridImageBeContained = (item: MediaManagerSDKFile, thumbMinWidth: number): boolean =>
  item.smartGridSize.ratio < thumbMinWidth / SMART_GRID_ROW_HEIGHT &&
  item.fileUrl &&
  getExtension(item.fileUrl) === 'png';

export const calculateItemsWrapperWidthAndHeight = (node: HTMLElement): { width: number; height: number } => {
  if (!node) {
    return { width: 0, height: 0 };
  }

  const paddingLeft = getComputedPropertyValueAsNumber(node, 'padding-left');
  const paddingRight = getComputedPropertyValueAsNumber(node, 'padding-right');

  return {
    width: node.offsetWidth - paddingLeft - paddingRight,
    height: window.innerHeight,
  };
};

const addMoreButtonAndPaddingWidth = add(WIDTH_WITHOUT_PRICE + PRICE_BUTTON_BORDER_AND_PADDING + PRICE_BUTTON_MARGIN);
export const createCalculateThumbMinWidth = (getItemPrice) =>
  memoize(
    pipe(
      getItemPrice,
      ifElse(
        isFalsy,
        always(WIDTH_WITHOUT_PRICE),
        pipe((price) => getTextWidth(price), addMoreButtonAndPaddingWidth),
      ),
      max(SMART_GRID_THUMB_MIN_WIDTH),
    ),
    getId,
  );

export const calculateFileStyle = memoize((width, minWidth) => ({ width, minWidth }));

export const generateSplitConfig = memoize((rowWidth, rowHeight, itemsGap, square) => ({
  rowWidth,
  rowHeight,
  itemsGap,
  square,
}));
